import { Eye, Pencil, XCircle } from "lucide-react";
import React from "react";

import {
  MAINTENANCE_STATUS_OPTIONS,
  MAINTENANCE_STATUS_VARIANT,
} from "../constants/maintenance.constants";
import { MaintenanceStatus, ReminderType } from "../maintenance.enums";
import { maintenanceMessages } from "../maintenance.intl";
import type { MaintenanceHistoryEntry } from "../types/maintenance.types";

import { ACTION_ID } from "@/app/constants/global-constants";
import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import { WpxDropdown } from "@/components/buttons/WpxActionButton";
import { Badge } from "@/components/ui/Badge";
import type { ExtendedColumnDef } from "@/interfaces/globals";
import {
  dateFormat,
  DAY_MONTH_YEAR_FORMAT_SHORTER,
  TIME_ONLY_FORMAT,
} from "@/utils/datetime/dateTimeHelper";

interface DateTimeCellProps {
  isoString: string | null;
  fallback?: string;
}

const getDurationHours = (
  startAt: string,
  endAt: string | null,
): string | null => {
  if (!endAt) {
    return null;
  }
  const start = new Date(startAt).getTime();
  const end = new Date(endAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return null;
  }
  const hours = Math.round((end - start) / 3_600_000);
  return `${hours}h`;
};

const DateTimeCell = ({
  isoString,
  fallback = "-",
}: DateTimeCellProps): React.JSX.Element => {
  const dateTitle = dateFormat(isoString, DAY_MONTH_YEAR_FORMAT_SHORTER);
  const timeSubtitle = dateFormat(isoString, TIME_ONLY_FORMAT);

  return (
    <div className='text-left'>
      <p className='text-gray-800 text-sm'>{dateTitle ?? fallback}</p>
      {timeSubtitle && timeSubtitle !== fallback && (
        <p className='text-gray-400 text-xs'>{timeSubtitle}</p>
      )}
    </div>
  );
};

interface UseMaintenanceHistoryColumnsProps {
  onViewEntry: (id: string) => void;
  onEditEntry?: (entry: MaintenanceHistoryEntry) => void;
  onCancelEntry?: (id: string) => void;
}

export const useMaintenanceHistoryColumns = (
  props: UseMaintenanceHistoryColumnsProps,
): ExtendedColumnDef<MaintenanceHistoryEntry>[] => {
  const { onViewEntry, onEditEntry, onCancelEntry } = props;
  const t = useIntlMessages(maintenanceMessages);

  const getActionOptions = React.useCallback(
    (row: { original: MaintenanceHistoryEntry }): ActionButtonDataTypes[] => {
      const options: ActionButtonDataTypes[] = [
        {
          icon: Eye,
          name: t("historyViewDetails"),
          onClick: () => onViewEntry(row.original.id),
        },
        {
          icon: Pencil,
          name: t("edit"),
          onClick: () => onEditEntry?.(row.original),
          hide:
            !onEditEntry || row.original.status !== MaintenanceStatus.SCHEDULED,
        },
        {
          icon: XCircle,
          name: t("abort"),
          isTrash: true,
          onClick: () => onCancelEntry?.(row.original.id),
          hide:
            !onCancelEntry ||
            row.original.status !== MaintenanceStatus.SCHEDULED,
        },
      ];

      return options;
    },
    [onViewEntry, onEditEntry, onCancelEntry, t],
  );

  return React.useMemo(
    () => [
      {
        sortBy: "startAt",
        accessorKey: "startAt",
        header: t("historyScheduledStart"),
        cell: ({ row }) => <DateTimeCell isoString={row.original.startAt} />,
        meta: {
          showRightBorder: true,
          minWidth: 160,
        },
      },
      {
        sortBy: "endAt",
        accessorKey: "endAt",
        header: t("historyScheduledEnd"),
        cell: ({ row }) => <DateTimeCell isoString={row.original.endAt} />,
        meta: {
          showRightBorder: true,
          minWidth: 160,
        },
      },
      {
        accessorKey: "reminderType",
        header: t("historyReminder"),
        cell: ({ row }): JSX.Element => {
          const { reminderType, reminderAt } = row.original;
          if (reminderType === "NONE") {
            return <span className='text-gray-800 text-sm'>-</span>;
          }
          if (reminderType === ReminderType.IMMEDIATE) {
            return (
              <span className='text-gray-800 text-sm whitespace-nowrap'>
                {t("historyImmediately")}
              </span>
            );
          }
          return <DateTimeCell isoString={reminderAt} />;
        },
        meta: {
          showRightBorder: true,
          minWidth: 160,
        },
      },
      {
        accessorKey: "notice",
        header: t("historyNotice"),
        cell: ({ row }): JSX.Element => (
          <p
            className='text-gray-800 text-sm line-clamp-2 max-w-[300px]'
            title={row.original.notice ?? undefined}
          >
            {row.original?.notice || "-"}
          </p>
        ),
        meta: {
          showRightBorder: true,
          minWidth: 200,
        },
      },
      {
        accessorKey: "updatedAt",
        header: t("historyReleasedOn"),
        cell: ({ row }): JSX.Element => {
          const { status, updatedAt } = row.original;
          if (status !== MaintenanceStatus.RELEASED) {
            return <span className='text-gray-800 text-sm'>-</span>;
          }
          return <DateTimeCell isoString={updatedAt} />;
        },
        meta: {
          showRightBorder: true,
          minWidth: 160,
        },
      },
      {
        id: "duration",
        header: t("historyDuration"),
        cell: ({ row }): JSX.Element => {
          const { startAt, endAt } = row.original;
          const duration = getDurationHours(startAt, endAt);
          return <Badge variant='secondary'>{duration ?? "-"}</Badge>;
        },
        meta: {
          showRightBorder: true,
          minWidth: 100,
        },
      },
      {
        accessorKey: "status",
        header: t("historyStatus"),
        cell: ({ row }) => (
          <div className='flex items-center justify-between gap-3'>
            <Badge variant={MAINTENANCE_STATUS_VARIANT[row.original.status]}>
              {
                MAINTENANCE_STATUS_OPTIONS.find(
                  (opt) => opt.id === row.original.status,
                )?.label
              }
            </Badge>
          </div>
        ),
      },
      {
        id: ACTION_ID,
        cell: ({ row }): React.JSX.Element => {
          return (
            <div className='flex justify-end '>
              <WpxDropdown data={getActionOptions(row)} />
            </div>
          );
        },
      },
    ],
    [t, getActionOptions],
  );
};
